import InlineSources from "@/modules/agent/components/review-responses/InlineSources";
import { VENDOR_PREDEFINED_COMPLIANCE_CERTS } from "@/modules/agent/constants/vendorAssessmentConstants";
import { ReviewSourceTypes, WebReviewSource } from "@/modules/agent/types";
import {
  BreachHistoryEvent,
  CompanyDrawerData,
  ComplianceItem,
  DarkWebChatterFinding,
  NewsArticle,
} from "@/modules/agent/types/vendorCompanyData";
import ZAvatarGradient from "@/shared/components/avatarGradient";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader
} from "@/shared/components/drawer";
import FilePreview from "@/shared/components/FilePreview";
import { Avatar, Button } from "@nextui-org/react";
import dayjs from "dayjs";
import { X } from "lucide-react";
import React, { useState } from "react";

interface CompanyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  drawerType: string | null;
  companyData: CompanyDrawerData;
}

// Define specific drawer content types
interface BreachHistoryDrawerContent {
  title: string;
  subtitle: string;
  count: number;
  label: string;
  items: BreachHistoryEvent[];
}

interface DarkWebChatterDrawerContent {
  title: string;
  subtitle: string;
  count: number;
  label: string;
  items: DarkWebChatterFinding[];
}

interface ComplianceDrawerContent {
  title: string;
  subtitle: string;
  count: number;
  label: string;
  items: ComplianceItem[];
}

interface NewsDrawerContent {
  title: string;
  subtitle: string;
  count: number;
  label: string;
  items: NewsArticle[];
}

const CompanyDrawer: React.FC<CompanyDrawerProps> = ({ 
  isOpen, 
  onClose, 
  drawerType, 
  companyData 
}) => {
  // Add state to track the selected source and sources list
  const [selectedSource, setSelectedSource] = useState<ReviewSourceTypes | undefined>();
  const [listOfSourcesForSelectedSources, setListOfSourcesForSelectedSources] = useState<ReviewSourceTypes[] | undefined>();

  // Source handlers
  const onSourceClick = (source: ReviewSourceTypes) => {
    // The InlineSources component's Avatar click handler calls this function directly
    // without checking if it's a web source, so we need to check here

    if ((source as WebReviewSource).is_web_source && (source as WebReviewSource).web_source_url) {
      // Open the web source URL in a new tab
      window.open((source as WebReviewSource).web_source_url, '_blank');
    } else {
    // For non-web sources, show the file preview
      setSelectedSource(source);
    }
  };

  // This function is used by InlineSources component for the group of sources
  const handleSetListOfSourcesForSelectedSources = (sources: ReviewSourceTypes[]) => {
    setListOfSourcesForSelectedSources(sources);
  };

  // Function to handle closing the source preview
  const handleCloseSourcePreview = () => {
    setSelectedSource(undefined);
    setListOfSourcesForSelectedSources(undefined);
  };

  // Render source preview if a source is selected
  const renderSourcePreview = () => {
    if (!selectedSource) return null;

    // Implement source preview rendering based on source type
    // This would be similar to renderSource in edit-section.tsx
    // For now, let's return a placeholder
    return (
      <div>
        {/* Render appropriate viewer based on file type */}
        {/* This would include PDFViewer, ImageViewer, JSONPreviewHighlight, ExcelPreview etc. */}
        {/* Each would get the selectedSource, listOfSourcesForSelectedSources, and onClose props */}
        Source preview for {selectedSource.file_name || selectedSource.source_file_name}
      </div>
    );
  };

  // Title and subtitle mapping based on drawer type
  const drawerContent = {
    breachHistory: {
      title: "Breach History",
      subtitle: "Compiled from various sources on the internet",
      count: companyData.breachHistory.count,
      label: companyData.breachHistory.label,
      items: companyData.breachHistory.items,
    } as BreachHistoryDrawerContent,

    darkWebChatter: {
      title: "Dark Web Chatter",
      subtitle: "Compiled from various sources on the internet",
      count: companyData.darkWebChatter.count,
      label: companyData.darkWebChatter.label,
      items: companyData.darkWebChatter.items,
    } as DarkWebChatterDrawerContent,

    compliance: {
      title: "Compliance",
      subtitle: "",
      count: companyData.compliance.count,
      label: companyData.compliance.label,
      items: companyData.compliance.items,
    } as ComplianceDrawerContent,

    news: {
      title: "News",
      subtitle: "",
      count: companyData.news.count,
      label: companyData.news.label,
      items: companyData.news.items,
    } as NewsDrawerContent
  };

  // Get content based on drawer type
  const content = drawerType ? drawerContent[drawerType as keyof typeof drawerContent] : null;

  const renderContent = () => {
    if (!drawerType || !content) return null;

    switch (drawerType) {
      case 'breachHistory': {
        const typedContent = content as BreachHistoryDrawerContent;
        return (
          <div className="flex flex-col gap-6 p-4">
            {typedContent.items.map((event, index) => (
              <div key={index} className="border-b border-[#E4E4E7] pb-6 last:border-b-0">
                <p className="text-sm font-medium mb-1">{event.title} ({event.date.split(' ').pop()})</p>
                <p className="text-sm text-[#777780]">{event.text}</p>
                <div className="relative h-6 mt-3">
                  <InlineSources
                    sources={event.sources}
                    onSourceClick={onSourceClick}
                    setListOfSourcesForSelectedSources={handleSetListOfSourcesForSelectedSources}
                    tooltipPlacement="bottom"
                    position="relative bottom-0"
                  />
                </div>
              </div>
            ))}
          </div>
        );
      }

      case 'darkWebChatter': {
        const typedContent = content as DarkWebChatterDrawerContent;
        return (
          <div className="flex flex-col gap-6 p-4">
            {typedContent.items.map((finding, index) => (
              <div key={index} className="border-b border-[#E4E4E7] pb-6 last:border-b-0">
                <p className="text-sm font-medium mb-2">{finding.title}</p>
                <p className="text-sm text-[#777780]">{finding.text}</p>
                <div className="relative h-6 mt-3">
                  <InlineSources
                    sources={finding.sources}
                    onSourceClick={onSourceClick}
                    setListOfSourcesForSelectedSources={handleSetListOfSourcesForSelectedSources}
                    tooltipPlacement="bottom"
                    position="relative bottom-0"
                  />
                </div>
              </div>
            ))}
          </div>
        );
      }

      case 'compliance': {
        const typedContent = content as ComplianceDrawerContent;
        return (
          <div className="p-4">
            <div>
              {typedContent.items.map((cert, index) => (
                <div key={index} className="border-b border-[#E4E4E7] py-4 flex items-center gap-4">
                  {/* Certification Icon - using logos when available */}
                  {(() => {
                    // Look for matching logo in predefined certs
                    const img = VENDOR_PREDEFINED_COMPLIANCE_CERTS.find(
                      (predefinedCert) => predefinedCert.label.toLowerCase() === cert.name.toLowerCase()
                    )?.logo;

                    return img ? (
                      <Avatar
                        size="lg"
                        name={cert.name}
                        src={img}
                        className="max-w-10 max-h-10"
                      />
                    ) : (
                      // Fallback to using ZAvatarGradient if available
                      typeof ZAvatarGradient !== 'undefined' ? (
                        <ZAvatarGradient
                          name={cert.name}
                          textSize={14}
                          size={40}
                          backgroundColor="#E4E4E7"
                          shadow="shadow-none"
                          textColor="text-[#171717]"
                        />
                      ) : (
                        // Or default to simple circular div with initials
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium overflow-hidden">
                          {cert.name.slice(0, 3)}
                        </div>
                      )
                    );
                  })()}

                  <div className="flex-grow">
                    <p className="text-sm font-medium text-[#171717]">{cert.name}</p>
                  </div>

                  {/* Source indicators */}
                  <div className="flex items-center gap-1">
                    <InlineSources
                      sources={cert.sources}
                      onSourceClick={onSourceClick}
                      setListOfSourcesForSelectedSources={setListOfSourcesForSelectedSources}
                      tooltipPlacement="left"
                      position="relative bottom-0"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'news': {
        const typedContent = content as NewsDrawerContent;
        return (
          <div className="flex flex-col gap-6 p-4">
            {typedContent.items.map((article, index) => (
              <div key={index} className="border-b border-[#E4E4E7] pb-6 last:border-b-0">
                {article.date && (
                  <p className="text-xs text-[#777780] mb-2">
                    {dayjs(article.date).format("D MMMM, YYYY")}
                  </p>
                )}
                <p className="text-sm font-medium mb-1">{article.title}</p>
                <p className="text-sm text-[#777780]">{article.text}</p>
                <div className="relative h-6 mt-3">
                  <InlineSources
                    sources={article.sources}
                    onSourceClick={onSourceClick}
                    setListOfSourcesForSelectedSources={setListOfSourcesForSelectedSources}
                    tooltipPlacement="bottom"
                    position="relative bottom-0"
                  />
                </div>
              </div>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={"right" as any}
        hideCloseButton
      >
        <DrawerContent className="h-full flex flex-col rounded-r-none border-l border-[#E4E4E7]">
          <DrawerHeader className="border-b border-[#E4E4E7] p-4 flex flex-col">
            <div className="flex justify-between items-start w-full">
              <h2 className="text-lg font-medium">{content?.title}</h2>
              <Button
                isIconOnly
                variant="light"
                onPress={onClose}
                className="w-4 h-4 text-[#777780] data-[hover=true]:bg-transparent data-[focus=true]:bg-transparent data-[focus-visible=true]:outline-none"
              >
                <X size={16} />
              </Button>
            </div>
            <p className="text-sm font-light text-[#777780]">{content?.subtitle}</p>
          </DrawerHeader>

          <DrawerBody className="overflow-y-auto p-0 flex-grow">
            {renderContent()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Only render FilePreview for non-web sources */}
      {selectedSource && !(selectedSource as WebReviewSource).is_web_source && (
        <FilePreview
          selectedSource={selectedSource}
          listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
          onClose={handleCloseSourcePreview}
          setSelectedSource={setSelectedSource}
        />
      )}
    </>
  );
};

export default CompanyDrawer;